import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AddArticleDTO,
  GetArticleResponseDTO,
  UpdateArticleDTO,
} from '../admin.dto';
import { Subscription } from 'rxjs';
import {
  AlertController,
  IonModal,
  LoadingController,
  ToastController,
} from '@ionic/angular';
import { AdminService } from '../admin.service';

@Component({
  selector: 'app-articles',
  templateUrl: './articles.page.html',
  styleUrls: ['./articles.page.scss'],
})
export class ArticlesPage implements OnInit {
  isLoading = true;
  loadedArticles: GetArticleResponseDTO[];
  articlesSub: Subscription;
  addArticleSub: Subscription;
  updateArticleSub: Subscription;
  showImageError = false;
  isPreviewMode = false;
  sendNotif = true;
  isModalOpen = false;
  isUpdate = false;
  selectedTitle = '';
  selectedAddedBy = '';
  selectedContent = '';
  uploadedImage = '';
  selectedId = 0;
  originalImage = '';
  @ViewChild('newArticleModal') newArticleModal: IonModal;
  constructor(
    private adminService: AdminService,
    private toastController: ToastController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController
  ) { }

  ngOnInit() {
    this.getArticles();
  }

  AddNew() {
    this.ClearFields();
    this.isUpdate = false;
    this.isModalOpen = true;
  }
  ngOnDestroy() {
    if (this.articlesSub) this.articlesSub.unsubscribe();
    if (this.addArticleSub) this.addArticleSub.unsubscribe();
    if (this.updateArticleSub) this.updateArticleSub.unsubscribe();
  }
  getArticles() {
    this.isLoading = true;
    this.articlesSub = this.adminService
      .getAllArticles()
      .subscribe((articles) => {
        this.loadedArticles = articles;
        this.isLoading = false;
      });
  }
  cancel() {
    if (this.isPreviewMode) {
      this.isPreviewMode = false;
    } else {
      this.isModalOpen = false;
      this.ClearFields();
    }
  }
  onPhotoCaptured(event: { imageUri: string }) {
    this.uploadedImage = event.imageUri;
  }
  ClearFields() {
    this.uploadedImage = '';
    this.showImageError = false;
    this.isPreviewMode = false;
    this.selectedAddedBy = '';
    this.selectedContent = '';
    this.selectedTitle = '';
    this.selectedId = 0;
    this.sendNotif = true;
  }
  ViewArticle(articleId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.articlesSub = this.adminService
          .getArticleById(articleId)
          .subscribe((article: GetArticleResponseDTO) => {
            this.selectedId = article.id;
            this.uploadedImage = article.image;
            this.originalImage = article.image;
            this.selectedAddedBy = article.addedBy;
            this.selectedContent = article.content;
            this.selectedTitle = article.title;
            this.isUpdate = true;
            this.isModalOpen = true;
            loadingEl.dismiss();
          });
      });
  }
  previewArticle() {
    if (this.uploadedImage.length == 0) {
      this.showImageError = true;
    } else {
      this.isPreviewMode = true;
    }
  }
  UpdateArticle() {
    const updatedArticle: UpdateArticleDTO = {
      id: this.selectedId,
      title: this.selectedTitle,
      content: this.selectedContent,
      addedBy: this.selectedAddedBy,
      image: this.uploadedImage,
    };
    this.loadingCtrl
      .create({
        keyboardClose: true,
        showBackdrop: false,
        message: 'Please Wait...',
      })
      .then(async (loadingEl) => {
        loadingEl.present();
        this.updateArticleSub = (
          await this.adminService.updateArticle(updatedArticle, this.originalImage)
        ).subscribe({
          next: async (isAdded) => {
            if (!isAdded) {
              loadingEl.dismiss();
              this.alertCtrl
                .create({
                  header: 'An Error Occurred',
                  message:
                    'Could not perform this action at the moment. Please try again later.',
                  buttons: [{ text: 'Dismiss' }],
                })
                .then((alerEl) => {
                  loadingEl.dismiss();
                  alerEl.present();
                });
            } else {
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Article updated successfully',
              });
              this.getArticles();
              loadingEl.dismiss();
              await toast.present();
              this.ClearFields();
              this.isUpdate = false;
              this.isModalOpen = false;
            }
          },
          error: () => loadingEl.dismiss(),
        });
      });
  }

  deleteArticle(articleId: number) {
    this.alertCtrl
      .create({
        header: 'Delete Article?',
        message: 'Are you sure you want to delete this article?',
        buttons: [
          {
            text: 'Cancel',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Delete',
            cssClass: 'confirm-delete',
            handler: () => {
              this.performDelete(articleId);
              this.alertCtrl.dismiss();
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  performDelete(articleId: number) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.originalImage = this.loadedArticles.find((e) => e.id === articleId)
          .image;
        this.adminService.deleteArticle(articleId, this.originalImage).subscribe({
          next: (res) => {
            if (res) {
              const index = this.loadedArticles.findIndex(
                (e) => e.id === articleId
              );
              this.loadedArticles.splice(index, 1);
            }
            loadingEl.dismiss();
          },
          error: () => {
            loadingEl.dismiss();
          },
        });
      });
  }

  AddArticle() {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        showBackdrop: false,
        message: 'Please Wait...',
      })
      .then(async (loadingEl) => {
        loadingEl.present();
        const article: AddArticleDTO = {
          title: this.selectedTitle,
          content: this.selectedContent,
          addedBy: this.selectedAddedBy,
          image: this.uploadedImage,
          sendNotification: this.sendNotif,
        };
        this.addArticleSub = (
          await this.adminService.addArticle(article)
        ).subscribe({
          next: async (isAdded) => {
            if (!isAdded) {
              loadingEl.dismiss();
              this.alertCtrl
                .create({
                  header: 'An Error Occurred',
                  message:
                    'Could not perform this action at the moment. Please try again later.',
                  buttons: [{ text: 'Dismiss' }],
                })
                .then((alerEl) => {
                  loadingEl.dismiss();
                  alerEl.present();
                });
            } else {
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Article added successfully',
              });
              this.getArticles();
              loadingEl.dismiss();
              await toast.present();
              this.isPreviewMode = false;
              this.cancel();
            }
          },
          error: () => loadingEl.dismiss(),
        });
      });
  }
}
